import { DatabaseZap, Pause, Play, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { ExportImport } from "@/components/migration/ExportImport";
import "@/migration.css";

const n = (v) => (v ?? 0).toLocaleString("fr-FR");
const pct = (a, b) => (b ? Math.min(100, Math.round((a / b) * 100)) : 0);
const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const nextText = (s) => {
  if (!s.auto) return "Transfert automatique désactivé.";
  if (s.up_to_date_today) return "Tout est à jour pour aujourd'hui.";
  const evening = new Date().toLocaleString("en-GB", { timeZone: "Europe/Paris", hour: "2-digit", hour12: false }) >= String(s.start_hour).padStart(2, "0");
  if (evening && !s.available_today) return "Quota du jour consommé : reprise demain soir.";
  return evening ? `Passage automatique en cours de créneau (depuis ${s.start_hour} h, heure de Paris).` : `Prochain passage automatique ce soir à ${s.start_hour} h (heure de Paris).`;
};

function Bar({ label, value, total, testid, note }) {
  return (
    <div className="mg-progress" data-testid={testid}>
      <div className="mg-progress-head"><span>{label}</span><strong>{n(value)} / {n(total)} · {pct(value, total)} %</strong></div>
      <div className="mg-track"><span style={{ width: `${pct(value, total)}%` }} /></div>
      {note && <small>{note}</small>}
    </div>
  );
}

function Quota({ q }) {
  const reserve = q.reserve_now;
  return (
    <section className="hm-card mg-quota" data-testid="mg-quota">
      <header><h3>Quota noCRM du jour</h3><small>Remis à zéro à minuit UTC (2 h l'été, 1 h l'hiver, heure de Paris)</small></header>
      {q.blocked_until && <p className="pl-warning" data-testid="mg-quota-blocked">noCRM a refusé les lectures : tout le quota du jour est épuisé chez noCRM ({n(q.used)} faites par le CRM, le reste par vos autres outils branchés sur noCRM). Reprise à {new Date(q.blocked_until).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}.</p>}
      <div className="mg-quota-row">
        <div><strong data-testid="mg-quota-used">{n(q.used)}</strong><small>utilisées sur {n(q.limit)}</small></div>
        <div><strong data-testid="mg-quota-remaining">{n(q.remaining)}</strong><small>restantes aujourd'hui</small></div>
        <div><strong data-testid="mg-quota-available">{n(q.available)}</strong><small>utilisables par le transfert maintenant (réserve {q.evening ? "du soir" : "de journée"} : {n(reserve)})</small></div>
      </div>
      <div className="mg-track big"><span style={{ width: `${pct(q.used, q.limit)}%` }} /><i style={{ left: `${pct(q.limit - reserve, q.limit)}%` }} title="Réserve" /></div>
    </section>
  );
}

function SettingsCard({ s, onSave }) {
  const [form, setForm] = useState({ auto: s.auto, start_hour: s.start_hour, reserve: s.reserve, day_reserve: s.day_reserve });
  const valid = ["reserve", "day_reserve"].every((k) => form[k] !== "" && Number(form[k]) >= 0 && Number(form[k]) <= 8000);
  const dirty = form.auto !== s.auto || Number(form.start_hour) !== s.start_hour || Number(form.reserve) !== s.reserve || Number(form.day_reserve) !== s.day_reserve;
  return (
    <section className="hm-card mg-settings" data-testid="mg-settings">
      <header><h3>Transfert automatique</h3></header>
      <label className="mg-switch"><input type="checkbox" checked={form.auto} onChange={(e) => setForm({ ...form, auto: e.target.checked })} data-testid="mg-auto" /> Lancer chaque soir automatiquement</label>
      <label>À partir de (heure de Paris)
        <select value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: Number(e.target.value) })} data-testid="mg-start-hour">
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h} h</option>)}
        </select>
      </label>
      <label>Réserve gardée le soir (à partir de l'heure choisie)
        <input type="number" min="0" max="8000" step="50" value={form.reserve} onChange={(e) => setForm({ ...form, reserve: e.target.value })} data-testid="mg-reserve" />
      </label>
      <label>Réserve gardée en journée (si vous lancez à la main avant l'heure)
        <input type="number" min="0" max="8000" step="100" value={form.day_reserve} onChange={(e) => setForm({ ...form, day_reserve: e.target.value })} data-testid="mg-day-reserve" />
      </label>
      {!valid && <p className="mg-error" data-testid="mg-reserve-error">Indiquez une réserve entre 0 et 8 000.</p>}
      <p className="mg-help">Le transfert consomme tout le quota restant de la journée (jusqu'à la réserve, ou jusqu'à ce que noCRM refuse), puis s'arrête au changement de jour du quota. Il reprend le soir suivant là où il s'était arrêté.</p>
      <button type="button" className="primary-button" disabled={!dirty || !valid} onClick={() => onSave({ ...form, start_hour: Number(form.start_hour), reserve: Number(form.reserve), day_reserve: Number(form.day_reserve) })} data-testid="mg-save">Enregistrer</button>
    </section>
  );
}

function Runs({ runs }) {
  return (
    <section className="hm-card mg-runs" data-testid="mg-runs">
      <header><h3>Derniers passages</h3></header>
      {!runs.length ? <p className="ck-muted">Aucun passage pour l'instant.</p> : (
        <table className="agent-table">
          <thead><tr><th>Début</th><th>Fin</th><th>Lancé</th><th>Requêtes</th><th>Fiches complétées</th><th>Fichiers</th><th>Arrêt</th></tr></thead>
          <tbody>{runs.map((r) => <tr key={r.started_at}><td>{when(r.started_at)}</td><td>{when(r.finished_at)}</td><td>{r.trigger === "auto" ? "Automatique" : "Manuel"}</td><td>{n(r.calls)}</td><td>{n(r.leads)}</td><td>{n(r.files)}</td><td className="mg-reason">{r.reason}</td></tr>)}</tbody>
        </table>
      )}
    </section>
  );
}

export default function MigrationPage() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/migration/status").then(({ data }) => setS(data)).catch(() => toast.error("Statut du transfert indisponible.")), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const act = (path, msg) => api.post(path).then(() => { toast.success(msg); setTimeout(load, 800); }).catch((e) => toast.error(e.response?.data?.detail || "Action impossible."));
  const save = (body) => api.put("/migration/settings", body).then(({ data }) => { setS(data); toast.success("Réglages enregistrés."); }).catch(() => toast.error("Réglages non enregistrés."));
  if (!s) return <section className="page"><p className="ck-muted">Chargement…</p></section>;
  const p = s.progress;
  const cur = s.current;
  return (
    <section className="page mg-page" data-testid="migration-page">
      <header className="mg-head">
        <div><p className="eyebrow">Paramètres</p><h1>Transfert de la base noCRM</h1><p className="page-subtitle">Copie complète dans l'application : fiches, champs, commentaires, historique et pièces jointes. noCRM n'est pas modifié.</p></div>
        <div className="mg-actions">
          <button type="button" className="secondary-button" onClick={load} data-testid="mg-refresh"><RefreshCw size={14} /> Actualiser</button>
          {s.running
            ? <button type="button" className="secondary-button" onClick={() => act("/migration/stop", "Arrêt demandé : la fiche en cours se termine.")} data-testid="mg-stop"><Pause size={14} /> Arrêter</button>
            : <button type="button" className="primary-button" disabled={s.quota.available <= 0} onClick={() => act("/migration/start", "Transfert lancé.")} data-testid="mg-start"><Play size={14} /> Lancer maintenant</button>}
        </div>
      </header>
      <div className={`mg-status ${s.running ? "on" : ""}`} data-testid="mg-status">
        <DatabaseZap size={18} />
        {s.running && cur ? <span>Transfert en cours depuis {when(cur.started_at)}.</span>
          : <span>{nextText(s)}{s.last_run ? ` Dernier passage : ${s.last_run.reason}` : ""}</span>}
      </div>
      <Quota q={s.quota} />
      <ExportImport />
      <section className="hm-card mg-progress-card" data-testid="mg-progress">
        <header><h3>Avancement</h3><small data-testid="mg-estimate">{s.estimate.calls_left ? `≈ ${n(s.estimate.calls_left)} requêtes restantes (pièces jointes comprises) · environ ${s.estimate.nights_left} soir(s) à ≈ ${n(s.estimate.nightly)} par soir` : "Tout est copié."}</small></header>
        <Bar label="Pipelines prioritaires (Call center LVB, Climatiseurs EDF, Campagnes Leds) · fiches ouvertes" value={p.priority_done} total={p.priority} testid="mg-bar-priority" note="Copiées en premier." />
        <Bar label="Fiche complète et commentaires : ouverture instantanée (2 requêtes par fiche)" value={p.essentials} total={Math.max(p.total, p.listed)} testid="mg-bar-leads" note="Ensuite le reste de ces pipelines, puis les autres fiches ouvertes, puis les fermées." />
        <Bar label="Historique et pièces jointes (1 requête par fiche + 1 par fichier)" value={p.detailed} total={Math.max(p.total, p.listed)} testid="mg-bar-details" note={`${n(p.files)} fichier(s) copié(s)${p.errors ? ` · ${n(p.errors)} fiche(s) à réessayer` : ""}${p.failed ? ` · ${n(p.failed)} fiche(s) abandonnée(s) après 3 essais` : ""}`} />
      </section>
      <SettingsCard key={`${s.auto}-${s.start_hour}-${s.reserve}-${s.day_reserve}`} s={s} onSave={save} />
      <Runs runs={s.runs} />
    </section>
  );
}
